import { onApplicationError } from 'actions/application.action'
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { TRACKING_UUID_KEY } from 'config'
import { getApiUrl } from 'config/tenant.runtime'
import isString from 'lodash/isString'
import { toast } from 'react-toastify'

import { store } from '../reducers'
import { isSessionSwitchInProgress } from '../utils/sessionSwitch'

class Api {
  static loanNumber: string
  static borrowerSeparator: string = ''
  static error: any = {}

  static setLoanNumber(num: string) {
    this.loanNumber = num
  }

  static getLoanNumber() {
    return this.loanNumber
  }

  static setBorrowerSeparator(borrowerSeparator: string) {
    this.borrowerSeparator = borrowerSeparator
  }

  static getBorrowerSeparator() {
    return this.borrowerSeparator
  }

  /**
   * GETs already in flight, so identical concurrent ones share a single request.
   *
   * Measured on a servicing screen 2026-09-21: one load issued 214 requests, and the repeats were
   * not a screen asking twice - they were several components each mounting and each fetching the
   * same thing in the same tick. `/ac/getNavBarData` went out 24 times, `unreadCount` 6.
   *
   * This coalesces rather than caches, which is the important distinction: a joined caller gets
   * the response of a request that is still outstanding, so it can be no more stale than making
   * the call itself. Nothing is held after the response arrives.
   *
   * It matters more than it looks. The API throttles 120 requests per 60 seconds per client IP,
   * and that one screen spent exactly 120 - it was sitting on the limit with no headroom.
   */
  private static inFlightGets = new Map<string, Promise<any>>()

  static get(route: string, data: any = {}, params: any = {}, options: any = {}) {
    Object.keys(data).forEach((key) => {
      if (data[key] && typeof data[key] === 'string') data[key] = data[key].replace(/'/g, "''")
    })

    // The loan number rides as a header, not in the URL, so two loans would otherwise collide on
    // the same key and one would receive the other's data. Prefer options.loanNumber when a caller
    // scopes the request to a specific loan without changing the global Api.loanNumber.
    let key: string | null = null
    try {
      key = JSON.stringify([route, params, data, options.loanNumber || this.loanNumber])
    } catch {
      // Unserialisable arguments (a File, a circular object) simply opt out rather than throw.
      key = null
    }
    if (key === null) return this.xhr(route, data, params, 'get', options)

    const existing = this.inFlightGets.get(key)
    if (existing) return existing

    const request = this.xhr(route, data, params, 'get', options).finally(() => {
      this.inFlightGets.delete(key as string)
    })
    this.inFlightGets.set(key, request)
    return request
  }

  static put(route: string, data: any = {}, params: any = {}, options: any = {}) {
    return this.xhr(route, data, params, 'put', options)
  }

  static patch(route: string, data: any = {}, params: any = {}, options: any = {}) {
    return this.xhr(route, data, params, 'patch', options)
  }

  static post(route: string, data: any = {}, params: any = {}, options: any = {}) {
    return this.xhr(route, data, params, 'post', options)
  }

  static delete(route: string, data: any = {}, params: any = {}, options: any = {}) {
    return this.xhr(route, data, params, 'delete', options)
  }

  static replaceVariables(route: string, params: any) {
    Object.keys(params).forEach((key) => {
      route = route.replace(`:${key}`, params[key])
    })
    return route
  }

  static getUtmParams() {
    const storedUuid = sessionStorage.getItem(TRACKING_UUID_KEY)
    if (!storedUuid) return {}

    const { href } = window.location
    const parsedUrl = new URL(href)
    const params = new URLSearchParams(parsedUrl.search)

    const data: Record<string, string> = {}
    params.forEach((value, key) => {
      if (!key.startsWith('utm_')) return
      data[key.replace('utm_', 'utm-')] = value
    })
    data['utm-id'] = storedUuid
    return data
  }

  static async wrapApiErrors(error: any = {}) {
    try {
      let { status, data } = error.response || {}
      if (!status) {
        throw new Error('Connection with API server is broken')
      }
      if (status === 401) {
        const state = store.getState()
        const {
          auth: { token },
        } = state
        if (token) {
          if (!isSessionSwitchInProgress()) {
            store.dispatch({ type: 'AUTH_LOGOUT' })
          }
          throw new Error('Unauthorized')
        }
      }
      if (typeof data == 'object' && data.text) data = await data.text()
      // A download (responseType blob) hands its error back as the raw JSON body. Read the server's
      // message out of it, or the toast shows `{"statusCode":400,"message":...}` verbatim.
      if (isString(data)) {
        try {
          const parsed = JSON.parse(data)
          if (parsed && typeof parsed === 'object') data = parsed
        } catch {
          // Not JSON: plain text is already the message.
        }
      }
      const { message } = data
      if (!message) {
        throw new Error(data)
      }

      if (isString(message)) {
        throw new Error(message)
      }
      if (status === 400) {
        let _message = ''
        message.map((item: string) => {
          _message += `${item}\n`
        })
        throw new Error(_message)
      }
      throw new Error('Unknown error')
    } catch (e: any) {
      console.log('API error', e)
      if (this.error !== e.message) {
        this.error = e.message
        setTimeout(() => {
          this.error = {}
        }, 3000)
        store.dispatch(onApplicationError(e))
      }
      throw e
    }
  }

  static xhr(route: string, data = {}, params = {}, method: string, defaultOptions: any = {}) {
    const sendRequest = (axiosInstance: AxiosInstance) => {
      const state = store.getState()
      const url = Api.replaceVariables(route, params)
      const headers: any = {
        'Content-Type': 'application/json',
        'loan-number': defaultOptions.loanNumber || this.loanNumber,
        'borrower-separator': defaultOptions.borrowerSeparator || this.borrowerSeparator,
        ...(defaultOptions.headers || {}),
      }

      const utmData = this.getUtmParams()
      Object.keys(utmData).forEach((key) => (headers[key] = utmData[key]))

      if (state.auth.token || (data as any).token) {
        headers.Authorization = `Bearer ${state.auth.token || (data as any).token}`
      }

      const options: any = {
        baseURL: getApiUrl(),
        url,
        method,
        headers,
        timeout: 25000,
        ...defaultOptions,
      }

      if (method === 'get') {
        options.params = data
      } else {
        options.data = data
      }

      return axiosInstance(options)
        .then((res) => {
          const { success, message } = res.data
          if (success !== undefined && message) {
            toast(message, { type: success ? 'success' : 'error' })
          }
          return res.data
        })
        .catch((err) => {
          return Api.wrapApiErrors(err)
        })
    }
    return sendRequest(axios.create())
  }

  static uploadFiles(route: string, data: any = {}, params: any = {}, files: Array<File>, defaultOptions: any = {}) {
    const sendRequest = (axiosInstance: AxiosInstance) => {
      const state = store.getState()
      const url = Api.replaceVariables(route, params)
      const headers: any = {
        'Content-Type': 'multipart/form-data',
        'loan-number': this.loanNumber,
      }

      if (state.auth.token) {
        headers.Authorization = `Bearer ${state.auth.token}`
      }

      var formData = new FormData()
      files.forEach((file, index) => formData.append(`files[${index}]`, file))
      Object.keys(data).forEach((key) => formData.append(key, data[key]))

      const options: AxiosRequestConfig<any> = {
        baseURL: getApiUrl(),
        url,
        method: 'post',
        headers,
        // timeout: 15000,
        data: formData,
        ...defaultOptions,
      }

      return axiosInstance(options)
        .then((res) => res.data)
        .catch((err) => {
          return Api.wrapApiErrors(err)
        })
    }
    return sendRequest(axios.create())
  }

  static postForm(route: string, formData: FormData, params: any = {}, defaultOptions: any = {}) {
    const sendRequest = (axiosInstance: AxiosInstance) => {
      const state = store.getState()
      const url = Api.replaceVariables(route, params)
      const headers: any = {
        'Content-Type': 'multipart/form-data',
        'loan-number': defaultOptions.loanNumber || this.loanNumber,
        'borrower-separator': this.borrowerSeparator,
      }

      if (state.auth.token) {
        headers.Authorization = `Bearer ${state.auth.token}`
      }

      const options: AxiosRequestConfig<any> = {
        baseURL: getApiUrl(),
        url,
        method: 'post',
        headers,
        data: formData,
        ...defaultOptions,
      }

      return axiosInstance(options)
        .then((res) => res.data)
        .catch((err) => {
          return Api.wrapApiErrors(err)
        })
    }
    return sendRequest(axios.create())
  }
}

/**
 * Api toasts every response that carries both `success` and `message`, so a caller that toasts
 * the same message shows it twice. Call this instead: it toasts `fallback` only when Api did not.
 */
export const toastUnlessApiShown = (res: any, fallback: string, type: 'success' | 'error') => {
  if (res?.success !== undefined && res?.message) return
  toast(fallback, { type })
}

export default Api
